import axios from 'axios';

const envApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
// Strip trailing slash and specific path to avoid double pathing (e.g. /api/pm/api/pm)
const API_BASE_URL = envApiUrl.replace(/\/$/, '').replace(/\/api\/pm$/, '');

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      localStorage.removeItem('accessToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================
// Landlord API
// ============================================
export interface LandlordProfile {
  id: string;
  tenantId: string;
  userId: string;
  fsmCustomerProfileId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  businessName?: string;
  businessNumber?: string;
  address?: LandlordAddress;
  notificationPreferences: NotificationPreferences;
  status: string;
  createdAt: string;
  updatedAt?: string;
  lastLoginAt?: string;
  fullName: string;
  displayName: string;
}

export interface LandlordAddress {
  line1: string;
  line2?: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
}

export interface NotificationPreferences {
  rentDueReminders: boolean;
  latePaymentAlerts: boolean;
  paymentReceived: boolean;
  maintenanceRequestNew: boolean;
  maintenanceQuoteReceived: boolean;
  maintenanceScheduled: boolean;
  maintenanceCompleted: boolean;
  leaseExpiring: boolean;
  leaseSignatureRequired: boolean;
  newTenantMessages: boolean;
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
}

export const landlordApi = {
  getMyProfile: () => api.get<LandlordProfile>('/api/landlords/me'),

  register: (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    businessName?: string;
  }) => api.post<{ landlordId: string; message: string }>('/api/landlords/register', data),

  updateProfile: (data: {
    firstName: string;
    lastName: string;
    phone?: string;
    businessName?: string;
    businessNumber?: string;
    address?: LandlordAddress;
  }) => api.put('/api/landlords/me', data),
};

// ============================================
// Property API
// ============================================
export interface Property {
  id: string;
  tenantId: string;
  ownerId: string;
  name: string;
  address: PropertyAddress;
  type: string;
  unitCount: number;
  status: string;
  createdAt: string;
  updatedAt?: string;
  rentalMode?: 'LongTerm' | 'ShortTerm' | 'Hybrid';
  bookingSettings?: BookingSettings;
  listingDetails?: ListingDetails;
}

export interface BookingSettings {
  minStayNights: number;
  maxStayNights?: number;
  checkInTime: string;
  checkOutTime: string;
  maxGuests: number;
}

export interface ListingDetails {
  headline: string;
  description: string;
  houseRules: string;
  photos: string[];
}

export interface PropertyAddress {
  line1: string;
  line2?: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
}

export const propertyApi = {
  getAll: () => api.get<Property[]>('/api/pm/properties'),

  getById: (id: string) => api.get<Property>(`/api/pm/properties/${id}`),

  create: (data: {
    name: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    type?: string;
    unitCount?: number;
    propertyType?: 'MultiUnit' | 'SingleFamily';
    rentalMode?: string;
    pid?: string; // Optinal PID for Halifax Registry lookup
  }) => api.post<{
    id: string;
    registryStatus?: {
      isRegistered: boolean;
      pid?: string;
      registrationNumber?: string;
      unitCount?: number;
      message: string;
    };
  }>('/api/pm/properties', data),

  update: (id: string, data: Partial<Property>) =>
    api.put(`/api/pm/properties/${id}`, data),

  delete: (id: string) => api.delete(`/api/pm/properties/${id}`),
};

// ============================================
// Unit API
// ============================================
export interface Unit {
  id: string;
  propertyId: string;
  unitNumber: string;
  name?: string;
  bedrooms: number;
  bathrooms: number;
  squareFootage?: number;
  floor?: number;
  monthlyRent: number;
  securityDeposit?: number;
  currency: string;
  status: string;
  currentTenantId?: string;
  amenities: UnitAmenities;
  createdAt: string;
  updatedAt?: string;
}

export interface UnitAmenities {
  refrigerator: boolean;
  stove: boolean;
  dishwasher: boolean;
  microwave: boolean;
  washerDryer: boolean;
  washerDryerHookup: boolean;
  airConditioning: boolean;
  heating: boolean;
  fireplace: boolean;
  balcony: boolean;
  patio: boolean;
  storage: boolean;
  parking: boolean;
  parkingSpaces: number;
  waterIncluded: boolean;
  heatIncluded: boolean;
  electricityIncluded: boolean;
  internetIncluded: boolean;
  petsAllowed: boolean;
  petRestrictions?: string;
}

export const unitApi = {
  getByProperty: (propertyId: string) =>
    api.get<Unit[]>(`/api/pm/properties/${propertyId}/units`),

  getById: (propertyId: string, unitId: string) =>
    api.get<Unit>(`/api/pm/properties/${propertyId}/units/${unitId}`),

  create: (propertyId: string, data: {
    unitNumber: string;
    bedrooms: number;
    bathrooms: number;
    monthlyRent: number;
    name?: string;
    squareFootage?: number;
    floor?: number;
    securityDeposit?: number;
    amenities?: Partial<UnitAmenities>;
  }) => api.post<Unit>(`/api/pm/properties/${propertyId}/units`, data),

  update: (propertyId: string, unitId: string, data: Partial<Unit>) =>
    api.put<Unit>(`/api/pm/properties/${propertyId}/units/${unitId}`, data),

  delete: (propertyId: string, unitId: string) =>
    api.delete(`/api/pm/properties/${propertyId}/units/${unitId}`),
};

// ============================================
// Tenant API
// ============================================
export interface Tenant {
  id: string;
  landlordId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth?: string;
  emergencyContact?: EmergencyContact;
  currentUnitId?: string;
  moveInDate?: string;
  moveOutDate?: string;
  status: string;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
  fullName: string;
}

export interface EmergencyContact {
  name: string;
  phone: string;
  relationship?: string;
}

export const tenantApi = {
  getAll: (status?: string) =>
    api.get<Tenant[]>('/api/pm/tenants', { params: { status } }),

  getById: (id: string) => api.get<Tenant>(`/api/pm/tenants/${id}`),

  create: (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    dateOfBirth?: string;
    emergencyContact?: EmergencyContact;
    notes?: string;
  }) => api.post<Tenant>('/api/pm/tenants', data),

  update: (id: string, data: Partial<Tenant>) =>
    api.put<Tenant>(`/api/pm/tenants/${id}`, data),

  assignToUnit: (id: string, data: { unitId: string; moveInDate: string }) =>
    api.post<Tenant>(`/api/pm/tenants/${id}/assign`, data),

  moveOut: (id: string, data: { moveOutDate: string }) =>
    api.post<Tenant>(`/api/pm/tenants/${id}/moveout`, data),
};

// ============================================
// Dashboard API
// ============================================
export interface DashboardSummary {
  totalProperties: number;
  totalUnits: number;
  occupiedUnits: number;
  vacantUnits: number;
  occupancyRate: number;
  totalTenants: number;
  activeTenants: number;
  totalMonthlyRent: number;
  collectedThisMonth: number;
  outstandingBalance: number;
  collectionRate: number;
  upcomingLeaseExpirations: number;
  overdueRentCount: number;
  pendingMaintenanceRequests: number;
  recentActivity: DashboardActivity[];
}

export interface DashboardActivity {
  id: string;
  type: 'payment' | 'maintenance' | 'lease' | 'tenant';
  title: string;
  description: string;
  timestamp: string;
  status?: string;
}

export const dashboardApi = {
  getSummary: (upcomingDays = 30) =>
    api.get<DashboardSummary>('/api/pm/dashboard/summary', { params: { upcomingDays } }),
};

// ============================================
// Lease API
// ============================================
export interface Lease {
  id: string;
  propertyId: string;
  unitId: string;
  tenantId: string;
  startDate: string;
  endDate: string;
  monthlyRent: number;
  securityDeposit: number;
  status: 'Draft' | 'PendingSignature' | 'Active' | 'Expired' | 'Terminated';
  createdAt: string;
  updatedAt?: string;
}

export const leaseApi = {
  getAll: (status?: string) =>
    api.get<Lease[]>('/api/pm/leases', { params: { status } }),

  getById: (id: string) => api.get<Lease>(`/api/pm/leases/${id}`),

  create: (data: {
    propertyId: string;
    unitId: string;
    tenantId: string;
    startDate: string;
    endDate: string;
    monthlyRent: number;
    securityDeposit?: number;
  }) => api.post<Lease>('/api/pm/leases', data),

  activate: (id: string) => api.post(`/api/pm/leases/${id}/activate`),

  terminate: (id: string, data: { terminationDate: string; reason?: string }) =>
    api.post(`/api/pm/leases/${id}/terminate`, data),
};

// ============================================
// Work Order / Maintenance API
// ============================================
export interface WorkOrder {
  id: string;
  propertyId: string;
  unitId?: string;
  tenantId?: string;
  title: string;
  description: string;
  priority: 'Low' | 'Medium' | 'High' | 'Emergency';
  status: 'Open' | 'InProgress' | 'Scheduled' | 'Completed' | 'Cancelled';
  category: string;
  createdAt: string;
  scheduledDate?: string;
  completedDate?: string;
}

export const workOrderApi = {
  getAll: (status?: string) =>
    api.get<WorkOrder[]>('/api/pm/workorders', { params: { status } }),

  getById: (id: string) => api.get<WorkOrder>(`/api/pm/workorders/${id}`),

  create: (data: {
    propertyId: string;
    unitId?: string;
    tenantId?: string;
    title: string;
    description: string;
    priority?: string;
    category?: string;
  }) => api.post<WorkOrder>('/api/pm/workorders', data),

  updateStatus: (id: string, status: string) =>
    api.patch(`/api/pm/workorders/${id}/status`, { status }),
};

// ============================================
// Rent API
// ============================================
export interface RentLedgerEntry {
  id: string;
  date: string;
  type: string; // "Invoice" or "Payment"
  description: string;
  amountCad: number;
  balanceImpact: number;
  status: string;
  reference?: string;
}

export const rentApi = {
  getLedger: (tenantId: string) =>
    api.get<RentLedgerEntry[]>(`/api/pm/rent/ledger/${tenantId}`),

  recordPayment: (data: {
    invoiceId: string;
    amountCad: number;
    paidAt: string;
    reference?: string;
  }) => api.post('/api/pm/rent/payments', data),

  generateInvoice: (data: {
    leaseId: string;
    dueDate: string;
  }) => api.post<{ invoiceId: string }>('/api/pm/rent/invoices/generate', data),
};

// ============================================
// Calendar / Booking API
// ============================================

export interface DateAvailability {
  date: string;
  status: 'Available' | 'Booked' | 'Leased' | 'Blocked' | 'Tentative';
  price: number;
  referenceId?: string;
  notes?: string;
}

export const calendarApi = {
  getAvailability: (propertyId: string, startDate: string, endDate: string) =>
    api.get<Record<string, DateAvailability[]>>(`/api/pm/calendar/${propertyId}`, {
      params: { startDate, endDate },
    }),
};


// ============================================
// Compliance API
// ============================================
export const complianceApi = {
  getProfile: (propertyId: string) =>
    api.get<any>(`/api/pm/properties/${propertyId}/compliance`),
  // Using 'any' for now as we might need to cast to ComplianceProfile manually or import it if I could validly cross-reference, 
  // but sticking to simple modifications. ideally we import { ComplianceProfile } from '../types/compliance' but that might cause circular deps or path issues if not careful.
  // For this context, I will assume consuming code casts it or I can try to import it if I want to be strict.
  // Let's keep it simple and just do the runtime call definition here.

  updateProfile: (propertyId: string, data: any) =>
    api.put(`/api/pm/properties/${propertyId}/compliance`, data),
};

export default api;
